"use client"

import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import Swal from "sweetalert2"
import { CirclesWithBar } from "react-loader-spinner"
import { getCompanies, verifyCompany, rejectCompany, toggleCompanyStatus } from "../../api"

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api.voyagian.com"

// Color palette for initials avatar
const AVATAR_COLORS = [
  "#dc3545", // red
  "#17a2b8", // teal/cyan
  "#ffc107", // yellow/gold
  "#28a745", // green
  "#6f42c1", // purple
  "#fd7e14", // orange
  "#20c997", // mint
  "#e83e8c", // pink
]

// Get consistent color based on company name
const getAvatarColor = (name) => {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

const TruncatedText = ({ text, title, maxLength = 50 }) => {
  if (!text || text === "N/A") return "N/A"
  if (text.length <= maxLength) {
    return (
      <div className="text-truncate" style={{ maxWidth: "100%" }} title={text}>
        {text}
      </div>
    )
  }

  const showFullText = () => {
    Swal.fire({
      title: title,
      text: text,
      confirmButtonColor: "#05468f",
      confirmButtonText: "Close",
    })
  }

  return (
    <div className="d-flex align-items-center justify-content-between overflow-hidden">
      <span className="text-truncate flex-grow-1 me-1" title={text}>
        {text.substring(0, maxLength)}
      </span>
      <button
        onClick={showFullText}
        className="btn btn-link p-0 text-decoration-none"
        style={{ fontSize: "0.75rem", color: "#05468f", whiteSpace: "nowrap" }}
      >
        More
      </button>
    </div>
  )
}

export default function CompanyListTable() {
  const [companies, setCompanies] = useState([])
  const [loading, setLoading] = useState(true)
  const [imageErrors, setImageErrors] = useState({}) // Track failed images
  const [togglingId, setTogglingId] = useState(null)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1,
  })
  const [statusFilter, setStatusFilter] = useState("") // "", "pending", "approved", "rejected"

  // Fetch companies on mount / page or status change
  useEffect(() => {
    fetchCompanies()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, statusFilter])

  const fetchCompanies = async () => {
    try {
      setLoading(true)
      const response = await getCompanies(pagination.page, pagination.limit, statusFilter)
      console.log("[v0] API Response:", response)

      if (response.success) {
        // Backend shape: { success, message, data: { companies, pagination } }
        const companiesData = response.data?.companies || response.data || []
        const paginationData = response.data?.pagination || response.pagination

        console.log("[v0] Companies data:", companiesData)
        if (companiesData.length > 0) {
          console.log("[v0] First company full object:", JSON.stringify(companiesData[0], null, 2))
        }

        setCompanies(companiesData)
        if (paginationData) {
          setPagination((prev) => ({
            ...prev,
            ...paginationData,
          }))
        }
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Failed to fetch companies",
      })
    } finally {
      setLoading(false)
    }
  }

  // Handle verify company
  const handleVerify = async (companyId, companyName) => {
    const result = await Swal.fire({
      title: "Send Verification Link?",
      text: `Send verification link to "${companyName}"'s email?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Send Link",
      cancelButtonText: "Cancel",
    })

    if (result.isConfirmed) {
      try {
        const response = await verifyCompany(companyId)
        if (response.success) {
          Swal.fire({
            icon: "success",
            title: "Link Sent!",
            text: response.message || "Verification link sent to company email.",
            timer: 3000,
            showConfirmButton: false,
          })
          fetchCompanies()
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.message || "Failed to send verification link",
        })
      }
    }
  }

  // Handle reject company
  const handleReject = async (companyId, companyName) => {
    const result = await Swal.fire({
      title: "Reject Company?",
      text: `Are you sure you want to reject "${companyName}"?`,
      icon: "warning",
      input: "textarea",
      inputLabel: "Rejection Reason (optional)",
      inputPlaceholder: "Enter reason for rejection...",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Reject",
      cancelButtonText: "Cancel",
    })

    if (result.isConfirmed) {
      try {
        const response = await rejectCompany(companyId, result.value || "")
        if (response.success) {
          Swal.fire({
            icon: "success",
            title: "Rejected!",
            text: response.message || "Company has been rejected.",
            timer: 2000,
            showConfirmButton: false,
          })
          fetchCompanies()
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.message || "Failed to reject company",
        })
      }
    }
  }

  const handleToggleStatus = async (companyId, companyName, currentStatus) => {
    const newStatus = !currentStatus
    setTogglingId(companyId)

    try {
      const response = await toggleCompanyStatus(companyId, newStatus)
      if (response.success) {
        // Update the company in local state
        setCompanies((prev) =>
          prev.map((company) => (company._id === companyId ? { ...company, isActive: newStatus } : company)),
        )

        Swal.fire({
          icon: "success",
          title: newStatus ? "Enabled!" : "Disabled!",
          text: response.message || `Company has been ${newStatus ? "enabled" : "disabled"}.`,
          timer: 2000,
          showConfirmButton: false,
        })
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Failed to toggle company status",
      })
    } finally {
      setTogglingId(null)
    }
  }

  const handleImageError = (companyId, logoUrl) => {
    console.log("[v0] Image failed to load for company:", companyId)
    console.log("[v0] Failed logo URL:", logoUrl)
    setImageErrors((prev) => ({ ...prev, [companyId]: true }))
  }

  const getLogoUrl = (logoUrl) => {
    if (!logoUrl) return null
    if (logoUrl.startsWith("http")) return logoUrl
    const fullUrl = `${API_BASE_URL}${logoUrl}`
    console.log("[v0] Constructed logo URL:", fullUrl)
    return fullUrl
  }

  // Get status badge
  const getStatusBadge = (status, verificationPending) => {
    switch (status) {
      case "approved":
        return <span className="badge bg-success">Approved</span>
      case "rejected":
        return <span className="badge bg-danger">Rejected</span>
      case "pending":
      default:
        if (verificationPending) {
          return <span className="badge bg-info text-white">Link Sent</span>
        }
        return <span className="badge bg-warning text-dark">Pending</span>
    }
  }

  // Get company initials for avatar
  const getInitials = (name) => {
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 3)
  }

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    return new Date(dateString).toLocaleDateString()
  }

  if (loading) {
    return (
      <div className="text-center py-5 d-flex flex-column align-items-center justify-content-center">
        <CirclesWithBar
          height="100"
          width="100"
          color="#05468f"
          outerCircleColor="#05468f"
          innerCircleColor="#05468f"
          barColor="#05468f"
          ariaLabel="circles-with-bar-loading"
          visible={true}
        />
        <p className="mt-2">Loading companies...</p>
      </div>
    )
  }

  return (
    <>
      {/* Status Filter Bar */}
      <div className="d-flex justify-content-end mb-3">
        <div className="d-flex align-items-center">
          <label className="form-label mb-0 me-2">Status:</label>
          <select
            className="form-select form-select-sm"
            style={{ maxWidth: 180 }}
            value={statusFilter}
            onChange={(e) => {
              const value = e.target.value
              // reset to first page when filter changes
              setPagination((prev) => ({ ...prev, page: 1 }))
              setStatusFilter(value)
            }}
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-striped table-hover align-middle" style={{ width: "100%", tableLayout: "fixed" }}>
          <thead>
            <tr>
              <th style={{ width: "60px" }}>Logo</th>
              <th style={{ width: "150px" }}>Company Name</th>
              <th style={{ width: "120px" }}>Reg. Number</th>
              <th style={{ width: "120px" }}>Tax/VAT Number</th>
              <th style={{ width: "120px" }}>Date Established</th>
              <th style={{ width: "200px" }}>Address</th>
              <th style={{ width: "100px" }}>City</th>
              <th style={{ width: "100px" }}>Country</th>
              <th style={{ width: "100px" }}>Postal Code</th>
              <th style={{ width: "130px" }}>Phone</th>
              <th style={{ width: "180px" }}>Email</th>
              <th style={{ width: "150px" }}>Website</th>
              <th style={{ width: "100px" }}>Currency</th>
              <th style={{ width: "150px" }}>Applicable Taxes</th>
              <th style={{ width: "150px" }}>Primary Ports</th>
              <th style={{ width: "150px" }}>Operating Countries</th>
              <th style={{ width: "120px" }}>Time Zone</th>
              <th style={{ width: "150px" }}>Working Days / Hours</th>
              <th style={{ width: "200px" }}>About Us</th>
              <th style={{ width: "200px" }}>Vision</th>
              <th style={{ width: "200px" }}>Mission</th>
              <th style={{ width: "200px" }}>Purpose</th>
              <th style={{ width: "120px" }}>Social Links</th>
              <th style={{ width: "100px" }}>Status</th>
              <th style={{ width: "150px" }}>Enable/Disable</th>
              <th style={{ width: "150px" }}>Actions</th>
            </tr>
          </thead>

          <tbody style={{ fontSize: "0.85rem" }}>
            {companies.length === 0 ? (
              <tr>
                <td colSpan="26" className="text-center py-4">
                  No companies found.
                </td>
              </tr>
            ) : (
              companies.map((company) => {
                const logoField = company.logoUrl || company.logo
                console.log("[v0] Company:", company.companyName, "Logo field:", logoField)

                const applicableTaxes = Array.isArray(company.applicableTaxes)
                  ? company.applicableTaxes.join(", ")
                  : company.applicableTaxes || ""

                const primaryPorts = Array.isArray(company.operatingPorts)
                  ? company.operatingPorts.join(", ")
                  : company.operatingPorts || ""

                const operatingCountries = Array.isArray(company.operatingCountries)
                  ? company.operatingCountries.join(", ")
                  : company.operatingCountries || ""

                return (
                  <tr key={company._id}>
                    {/* Logo */}
                    <td className="text-center overflow-hidden">
                      {logoField && !imageErrors[company._id] ? (
                        <img
                          src={getLogoUrl(logoField) || "/placeholder.svg"}
                          alt={company.companyName}
                          onError={() => handleImageError(company._id, getLogoUrl(logoField))}
                          crossOrigin="anonymous"
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div
                          className="mx-auto"
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            backgroundColor: getAvatarColor(company.companyName),
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                            fontSize: "10px",
                          }}
                        >
                          {getInitials(company.companyName)}
                        </div>
                      )}
                    </td>

                    {/* Basic fields */}
                    <td className="text-truncate overflow-hidden" title={company.companyName}>
                      {company.companyName}
                    </td>
                    <td className="text-truncate overflow-hidden" title={company.registrationNumber}>
                      {company.registrationNumber || "N/A"}
                    </td>
                    <td className="text-truncate overflow-hidden" title={company.taxVatNumber}>
                      {company.taxVatNumber || "N/A"}
                    </td>
                    <td className="text-truncate overflow-hidden">{formatDate(company.dateEstablished)}</td>
                    <td className="overflow-hidden">
                      <TruncatedText text={company.address} title="Address" maxLength={40} />
                    </td>
                    <td className="text-truncate overflow-hidden">{company.city || "N/A"}</td>
                    <td className="text-truncate overflow-hidden">{company.country || "N/A"}</td>
                    <td className="text-truncate overflow-hidden">{company.postalCode || "N/A"}</td>
                    <td className="text-truncate overflow-hidden">{company.mainPhoneNumber || "N/A"}</td>
                    <td className="text-truncate overflow-hidden" title={company.emailAddress}>
                      {company.emailAddress || "N/A"}
                    </td>
                    <td className="text-truncate overflow-hidden">
                      {company.website ? (
                        <Link to={company.website} target="_blank" title={company.website}>
                          {company.website.replace(/^https?:\/\//, "")}
                        </Link>
                      ) : (
                        "N/A"
                      )}
                    </td>
                    <td className="text-center">{company.defaultCurrency || "N/A"}</td>

                    {/* Extra fields */}
                    <td className="overflow-hidden">
                      <TruncatedText text={applicableTaxes} title="Applicable Taxes" maxLength={30} />
                    </td>
                    <td className="overflow-hidden">
                      <TruncatedText text={primaryPorts} title="Primary Ports" maxLength={30} />
                    </td>
                    <td className="overflow-hidden">
                      <TruncatedText text={operatingCountries} title="Operating Countries" maxLength={30} />
                    </td>
                    <td className="text-truncate overflow-hidden">{company.timeZone || "N/A"}</td>
                    <td className="text-truncate overflow-hidden">{company.workingHours || "N/A"}</td>
                    <td className="overflow-hidden">
                      <TruncatedText text={company.whoWeAre} title="About Us" maxLength={50} />
                    </td>
                    <td className="overflow-hidden">
                      <TruncatedText text={company.vision} title="Vision" maxLength={50} />
                    </td>
                    <td className="overflow-hidden">
                      <TruncatedText text={company.mission} title="Mission" maxLength={50} />
                    </td>
                    <td className="overflow-hidden">
                      <TruncatedText text={company.purpose} title="Purpose" maxLength={50} />
                    </td>

                    {/* Social links */}
                    <td className="overflow-hidden">
                      <div className="d-flex gap-2 justify-content-start align-items-center h-100">
                        {company.facebookUrl && (
                          <Link to={company.facebookUrl} target="_blank">
                            <i className="bi bi-facebook"></i>
                          </Link>
                        )}
                        {company.instagramUrl && (
                          <Link to={company.instagramUrl} target="_blank">
                            <i className="bi bi-instagram"></i>
                          </Link>
                        )}
                        {company.whatsappNumber && (
                          <Link to={company.whatsappNumber} target="_blank">
                            <i className="bi bi-whatsapp"></i>
                          </Link>
                        )}
                        {company.linkedinProfile && (
                          <Link to={company.linkedinProfile} target="_blank">
                            <i className="bi bi-linkedin"></i>
                          </Link>
                        )}
                      </div>
                    </td>

                    {/* Status */}
                    <td>{getStatusBadge(company.status, company.verificationPending)}</td>

                    {/* Enable/Disable toggle column */}
                    <td>
                      {company.status === "approved" ? (
                        <div className="d-flex align-items-center gap-2">
                          <span style={{ fontSize: "0.85rem", minWidth: "60px" }}>
                            {company.isActive !== false ? "Enabled" : "Disabled"}
                          </span>
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              checked={company.isActive !== false}
                              onChange={() =>
                                handleToggleStatus(company._id, company.companyName, company.isActive !== false)
                              }
                              disabled={togglingId === company._id}
                              style={{ cursor: togglingId === company._id ? "wait" : "pointer" }}
                            />
                          </div>
                        </div>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>

                    {/* Actions */}
                    <td>
                      {company.status === "pending" && (
                        <>
                          <button
                            className="btn btn-sm btn-success me-1"
                            onClick={() => handleVerify(company._id, company.companyName)}
                          >
                            Verify
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleReject(company._id, company.companyName)}
                          >
                            Reject
                          </button>
                        </>
                      )}
                      {company.status === "approved" && (
                        <span className="text-success">
                          <i className="bi bi-check-circle"></i> Verified
                        </span>
                      )}
                      {company.status === "rejected" && (
                        <span className="text-danger">
                          <i className="bi bi-x-circle"></i> Rejected
                        </span>
                      )}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <nav className="mt-3">
            <ul className="pagination justify-content-center">
              <li className={`page-item ${pagination.page === 1 ? "disabled" : ""}`}>
                <button
                  className="page-link"
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                >
                  Previous
                </button>
              </li>
              {[...Array(pagination.pages)].map((_, index) => (
                <li key={index + 1} className={`page-item ${pagination.page === index + 1 ? "active" : ""}`}>
                  <button className="page-link" onClick={() => setPagination((prev) => ({ ...prev, page: index + 1 }))}>
                    {index + 1}
                  </button>
                </li>
              ))}
              <li className={`page-item ${pagination.page === pagination.pages ? "disabled" : ""}`}>
                <button
                  className="page-link"
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.pages}
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        )}
      </div>
    </>
  )
}
